import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  AUTOSCALING_ELIGIBILITY_PARAM_KEY,
  AUTOSCALING_STATE_PARAM_KEY,
  countAutoscalingFilterSelections,
  migrateLegacyAutoscalingUrlParams,
  stripAutoscalingFiltersFromApiFilters,
} from '~/shared/utils/autoscalingFilterUtils';
import { SelectOption } from '~/shared/utils/types';

import { apiFilterKey } from '../../../../common/serachParamKeys';

const TAG_FIELD_RE = /^tag:(.+)$/; // captures tag key after 'tag:'
const VALUE_SPLIT_RE = /\s*,\s*/; // split comma-separated values

export interface AppliedFiltersResult {
  appliedClusterNameFilters: SelectOption[];
  appliedInstanceFilters: SelectOption[];
  appliedVolumeIdFilters: SelectOption[];
  appliedVolumeTagFilters: SelectOption[];
  appliedAccountFilters: SelectOption[];
  appliedAutoscaleState: string[];
  appliedEligibility: string[];
  activeFilterCount: number;
  currentBaseFilters: SelectOption[];
  needsAutoscalingUrlMigration: boolean;
}

export function useAppliedFilters(): AppliedFiltersResult {
  const [searchParams] = useSearchParams();
  const apiFilters = searchParams.get(apiFilterKey);
  const currentBaseFilters = useMemo(
    () => JSON.parse(apiFilters || '[]') as SelectOption[],
    [apiFilters]
  );

  return useMemo(() => {
    const processedFilters = searchParams.get('processed_filters');
    const appliedClusterNameFilters: SelectOption[] = [];
    const appliedInstanceFilters: SelectOption[] = [];
    const appliedVolumeIdFilters: SelectOption[] = [];
    const appliedVolumeTagFilters: SelectOption[] = [];
    const appliedAccountFilters: SelectOption[] = [];

    if (processedFilters) {
      try {
        const parsedProcessedFilters = JSON.parse(processedFilters) as string[];
        parsedProcessedFilters.forEach((filterStr) => {
          const filter = JSON.parse(filterStr);
          const { field, value, op } = filter;
          if ((op === 'in' || op === 'equals') && typeof value === 'string') {
            const values = value.split(VALUE_SPLIT_RE);
            values.forEach((val: string) => {
              const cleanVal = val.trim();
              const tagFieldMatch = typeof field === 'string' ? TAG_FIELD_RE.exec(field) : null;
              const isTag = Boolean(tagFieldMatch);
              const tagKey = tagFieldMatch ? tagFieldMatch[1] : '';
              const compositeId = isTag ? `${tagKey}:${cleanVal}` : cleanVal;
              const option: SelectOption = {
                id: compositeId,
                name: isTag ? `${tagKey}:${cleanVal}` : `${cleanVal}`,
                group: field as SelectOption['group'],
              };
              if (field === 'clusterName') {
                appliedClusterNameFilters.push(option);
              }
              if (field === 'instanceId') {
                appliedInstanceFilters.push(option);
              }
              if (field === 'volumeId') {
                appliedVolumeIdFilters.push(option);
              }
              if (field === 'accountId') {
                appliedAccountFilters.push(option);
              }
              if (isTag) {
                appliedVolumeTagFilters.push(option);
              }
            });
          }
        });
      } catch {
        // swallow parse errors silently – fallback below
      }
    } else if (apiFilters) {
      appliedClusterNameFilters.push(
        ...currentBaseFilters.filter((f) => f.group === 'clusterName')
      );
      appliedInstanceFilters.push(...currentBaseFilters.filter((f) => f.group === 'instanceId'));
      appliedVolumeIdFilters.push(
        ...currentBaseFilters
          .filter((f) => f.group === 'volumeId')
          .map((f) => ({ ...f, name: `id:${f.name}` }))
      );
      appliedAccountFilters.push(...currentBaseFilters.filter((f) => f.group === 'accountId'));
      appliedVolumeTagFilters.push(
        ...currentBaseFilters.filter((f) => String(f.group).startsWith('tag:'))
      );
    }

    const autoscalingStateRaw = searchParams.get(AUTOSCALING_STATE_PARAM_KEY) || '';
    const eligibilityRaw = searchParams.get(AUTOSCALING_ELIGIBILITY_PARAM_KEY) || '';
    const rawStateIds = autoscalingStateRaw.split(',').filter(Boolean);
    const rawEligibilityIds = eligibilityRaw.split(',').filter(Boolean);

    const migrated = migrateLegacyAutoscalingUrlParams(
      rawStateIds,
      rawEligibilityIds,
      currentBaseFilters
    );

    const autoscalingCount = countAutoscalingFilterSelections(
      migrated.stateIds,
      migrated.eligibilityIds
    );
    const baseFilterCount = stripAutoscalingFiltersFromApiFilters(currentBaseFilters).length;
    const activeFilterCount = baseFilterCount + autoscalingCount;

    return {
      appliedClusterNameFilters,
      appliedInstanceFilters,
      appliedVolumeIdFilters,
      appliedVolumeTagFilters,
      appliedAccountFilters,
      appliedAutoscaleState: migrated.stateIds,
      appliedEligibility: migrated.eligibilityIds,
      activeFilterCount,
      currentBaseFilters,
      needsAutoscalingUrlMigration: migrated.didMigrate,
    };
  }, [apiFilters, currentBaseFilters, searchParams]);
}
